import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CallInfo, CallState, CallDirection, RegistrationState, WorkstationSession, WaitingCall, SipCredentials, SipCredentialsCheck, RegistrationRejection, CallEndReason, TransferResult, ConsultCall } from '../models/voip.models';
import { VoipWorkstationService } from './voip-workstation.service';
import * as SIP from 'sip.js';

/**
 * Powód odrzucenia rejestracji przez centralę, w kategoriach, na które da się
 * zareagować: dane konta są złe (do poprawy), centrala niedostępna (poczekać),
 * albo coś, czego nie znamy. Centrala odpowiada kodem z tych samych rodzin co HTTP.
 */
export function classifyRegistrationRejection(statusCode: number | undefined): RegistrationRejection {
    switch (statusCode) {
        // Złe hasło/login konta SIP (338) albo konto nieznane centrali.
        case 401:
        case 403:
        case 404:
            return 'bad-credentials';
        // Centrala żyje, ale teraz nie przyjmuje rejestracji.
        case 408:
        case 480:
        case 500:
        case 503:
            return 'pbx-unavailable';
        default:
            return 'unknown';
    }
}

const REGISTRATION_REJECTION_MESSAGES: { [reason in RegistrationRejection]: string } = {
    'bad-credentials': 'VOIP.REGISTER_REJECTED_AUTH',
    'pbx-unavailable': 'VOIP.REGISTER_REJECTED_UNAVAILABLE',
    'unknown': 'VOIP.REGISTER_REJECTED_OTHER'
};

/** Powód odrzucenia rejestracji jako klucz i18n — do pokazania operatorowi. */
export function registrationRejectionKey(statusCode: number | undefined): string {
    return REGISTRATION_REJECTION_MESSAGES[classifyRegistrationRejection(statusCode)];
}

export function classifyCallRejection(statusCode: number | undefined): CallEndReason {
    switch (statusCode) {
        case 486:
        case 600:
            return 'busy';
        case 603:
            return 'callee_rejected';
        case 408:
        case 480:
            return 'no_answer';
        case 404:
        case 410:
        case 484:
        case 604:
            return 'unreachable';
        default:
            return 'failed';
    }
}

@Injectable({
    providedIn: 'root'
})
export class SipService {

    private userAgent: any = null;
    private registerer: any = null;
    private currentSession: any = null;
    private callEndReason: CallEndReason | null = null;
    private audioElement: HTMLAudioElement | null = null;
    private durationInterval: any = null;
    // Stan wyciszenia mikrofonu — żeby wznowienie z hold nie odwyciszyło rozmowy.
    private callMuted: boolean = false;

    private session: WorkstationSession | null = null;

    // Observables
    private registrationState$ = new BehaviorSubject<RegistrationState>('unregistered');
    private callInfo$ = new BehaviorSubject<CallInfo>({
        state: 'idle',
        direction: null,
        remoteNumber: null,
        startTime: null,
        duration: 0
    });
    private incomingCall$ = new Subject<{ remoteNumber: string }>();
    // Powody, dla których telefon nie działa — jako klucze i18n, tłumaczone przez komponent.
    private error$ = new Subject<string>();

    // #372: kolejka połączeń przychodzących, które zastały telefon zajęty. Wcześniej drugie
    // INVITE nadpisywało `currentSession` i `callInfo$` — pierwszy dzwoniący przepadał bez śladu
    // (dzwonił do timeoutu trunku), a jego późniejsze Terminated czyściło stan TRWAJĄCEJ rozmowy.
    private waitingSessions: { id: number; invitation: any; remoteNumber: string; receivedAt: Date }[] = [];
    private waitingCalls$ = new BehaviorSubject<WaitingCall[]>([]);
    private waitingCallSeq = 0;
    // Połączenie, które czekało w kolejce i rozłączyło się, zanim ratownik do niego doszedł.
    private missedCall$ = new Subject<{ remoteNumber: string; receivedAt: Date; endReason: CallEndReason | null }>();

    // Click-to-call - żądanie wybrania numeru z zewnątrz (np. z listy pacjentów)
    private dialRequest$ = new Subject<{ phoneNumber: string; name?: string }>();
    private voipAvailable: boolean = false;

    // Emitowany po zakończeniu rozmowy (przed resetCallState)
    private callEnded$ = new Subject<{ remoteNumber: string; direction: CallDirection; duration: number; startTime: Date | null; endReason: CallEndReason | null }>();

    // Przekazanie z konsultacją: druga noga (rozmowa z odbiorcą) żyje OBOK currentSession.
    // callInfo$ zostaje przy dzwoniącym (stan 'on-hold'), więc alarm, historia rozmów
    // i kolejka oczekujących dalej widzą jedną rozmowę — SH nie ma drugiej linii jak TC.
    private consultSession: any = null;
    private consultCall$ = new BehaviorSubject<ConsultCall | null>(null);
    // „Połącz" wysłany: centrala kończy wtedy obie nasze nogi i koniec konsultacji
    // nie może wznawiać dzwoniącego z holdu.
    private consultTransferred: boolean = false;

    private boundBeforeUnload: (() => void) | null = null;

    // Ile czekamy na odpowiedź centrali przy sprawdzaniu danych konta SIP (338).
    private static readonly VERIFY_TIMEOUT_MS = 10000;
    // Adres centrali, gdy nie przyszedł razem z kontem SIP: backend nie podaje go dziś
    // managerowi. Te same wartości domyślne, co w encjach ParamedicSipAccount i SipWorkstation.
    private static readonly DEFAULT_SIP_SERVER = 'sip.supervoip.pl';
    private static readonly DEFAULT_WS_PORT = 7543;

    // 426: zerwane połączenie z centralą nie wraca samo. sip.js domyślnie nie ponawia go
    // wcale (reconnectionAttempts = 0), a jego własne ponawianie jest przestarzałe i odtwarza
    // sam transport, bez rejestracji — czyli to, co decyduje, czy telefon dzwoni i odbiera.
    // Odstępy rosną: krótka przerwa w sieci wraca od razu, dłuższa awaria centrali nie dostaje
    // żądania co dwie sekundy. Po ostatniej próbie telefon mówi wprost, że nie ma połączenia.
    private static readonly RECONNECT_DELAYS_MS = [2000, 5000, 10000, 20000, 30000];
    private reconnectAttempt: number = 0;
    private reconnectTimer: any = null;

    constructor(
        private ngZone: NgZone,
        private workstationService: VoipWorkstationService
    ) {
        // Utwórz element audio do odtwarzania głosu
        this.audioElement = document.createElement('audio');
        this.audioElement.autoplay = true;
    }

    /**
     * Postaw telefon od zera — wejście dla operatora i dla startu widżetu. Ubija próbę
     * zaplanowaną przez automat i oddaje mu pełną pulę ponowień; automat ponawia przez
     * connectToPbx(), żeby nie kasować sobie postępu serii.
     */
    async initialize(): Promise<boolean> {
        this.cancelReconnect();
        this.reconnectAttempt = 0;
        return this.connectToPbx();
    }

    private async connectToPbx(): Promise<boolean> {
        try {
            // Defensywnie posprzątaj pozostałości po poprzednim (np. nieudanym)
            // połączeniu. Osierocony UserAgent/WebSocket trzymał rejestrację SIP,
            // przez co ponowne łączenie po rozłączeniu wymagało odświeżenia strony.
            if (this.userAgent || this.registerer) {
                await this.disconnect();
            }

            // Pobierz sesję z backendu
            const session = await this.workstationService.getMySession().toPromise();

            if (!session || !session.hasSession) {
                console.log('SIP: No active session');
                return false;
            }

            this.session = session;

            console.log('SIP Session data:', {
                sipLogin: session.sipLogin,
                sipServer: session.sipServer,
                wsPort: session.wsPort,
                workstationName: session.workstationName
            });

            const wsUrl = `wss://${session.sipServer}:${session.wsPort}`;
            console.log('SIP connecting to:', wsUrl);

            const uri = SIP.UserAgent.makeURI(`sip:${session.sipLogin}@${session.sipServer}`);

            if (!uri) {
                console.error('Failed to create SIP URI');
                return false;
            }

            this.userAgent = this.createUserAgent({
                uri: uri,
                transportOptions: {
                    server: wsUrl
                },
                authorizationUsername: session.sipLogin,
                authorizationPassword: session.sipPassword,
                displayName: session.workstationName || session.sipLogin,
                delegate: {
                    onInvite: (invitation: any) => {
                        this.ngZone.run(() => {
                            this.handleIncomingCall(invitation);
                        });
                    },
                    onDisconnect: (error?: Error) => {
                        this.ngZone.run(() => {
                            this.handleTransportLost(error);
                        });
                    }
                }
            });

            // Uruchom UserAgent
            await this.userAgent.start();

            // Rejestracja
            this.registrationState$.next('registering');
            this.registerer = this.createRegisterer(this.userAgent);

            this.registerer.stateChange.addListener((state: any) => {
                console.log('SIP Registration state changed:', state);
                this.ngZone.run(() => {
                    switch (state) {
                        case SIP.RegistererState.Registered:
                            console.log('SIP: Successfully registered! Ready to receive calls.');
                            this.reconnectAttempt = 0;
                            this.registrationState$.next('registered');
                            break;
                        case SIP.RegistererState.Unregistered:
                            console.log('SIP: Unregistered');
                            this.registrationState$.next('unregistered');
                            break;
                    }
                });
            });

            // #339: bez `requestDelegate` odrzucona rejestracja (np. błędne hasło konta SIP)
            // znikała bez śladu — sip.js przestawiał registerera na Unregistered, telefon
            // pokazywał „Rozłączono" i nikt nie wiedział, że konto jest źle skonfigurowane.
            await this.registerer.register({
                requestDelegate: {
                    onReject: (response: any) => {
                        this.ngZone.run(() => this.handleRegistrationRejected(response));
                    }
                }
            });

            // Cleanup SIP registration on page close/refresh to avoid "Too many contacts"
            this.boundBeforeUnload = () => {
                if (this.registerer) {
                    try { this.registerer.unregister(); } catch (e) {}
                }
                if (this.userAgent) {
                    try { this.userAgent.stop(); } catch (e) {}
                }
            };
            window.addEventListener('beforeunload', this.boundBeforeUnload);

            return true;

        } catch (error) {
            console.error('SIP initialization error:', error);
            // Posprzątaj częściowo utworzony UserAgent/Registerer, żeby nie blokował
            // kolejnych prób połączenia (inaczej trzeba było odświeżać stronę).
            if (this.registerer) {
                try { await this.registerer.unregister(); } catch (e) {}
            }
            if (this.userAgent) {
                try { await this.userAgent.stop(); } catch (e) {}
            }
            this.userAgent = null;
            this.registerer = null;
            this.registrationState$.next('error');
            // Techniczny opis błędu został w konsoli — operatorowi idzie komunikat,
            // z którego wynika, co ma zrobić (error$ niesie klucze i18n).
            this.error$.next('VOIP.NO_PBX_CONNECTION');
            return false;
        }
    }

    /**
     * 426: centrala albo sieć zerwały transport. Rozłączenie bez błędu jest nasze własne
     * (disconnect przy wylogowaniu, zamknięcie strony) i nie ma czego wznawiać.
     */
    private handleTransportLost(error?: Error): void {
        if (!error) {
            return;
        }
        console.error('SIP: connection to the PBX lost', error);
        this.scheduleReconnect();
    }

    /**
     * Kolejna próba postawienia telefonu od zera. connectToPbx() sprząta po poprzednim
     * połączeniu i pobiera sesję na nowo, więc telefon nie wróci na stanowisko, z którego
     * operatora w międzyczasie wylogowano.
     */
    private scheduleReconnect(): void {
        const delay = SipService.RECONNECT_DELAYS_MS[this.reconnectAttempt];
        if (delay === undefined) {
            this.registrationState$.next('error');
            this.error$.next('VOIP.NO_PBX_CONNECTION');
            return;
        }

        this.reconnectAttempt++;
        this.registrationState$.next('registering');
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connectToPbx()
                .then(connected => {
                    if (!connected) {
                        this.scheduleReconnect();
                    }
                })
                .catch(error => {
                    // connectToPbx() sprząta po sobie i zwraca false, więc tu nie powinniśmy
                    // trafić. Bez tej gałęzi jeden nieprzewidziany wyjątek uciszyłby całe
                    // odzyskiwanie połączenia — telefon zostałby martwy bez śladu.
                    console.error('SIP: reconnect attempt failed', error);
                    this.scheduleReconnect();
                });
        }, delay);
    }

    private cancelReconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * #339: centrala odrzuciła REGISTER. sip.js zdążył już przestawić registerera na
     * Unregistered, więc nadpisujemy stan na 'error' i podajemy powód — inaczej telefon
     * milczy z napisem „Rozłączono".
     */
    private handleRegistrationRejected(response: any): void {
        const statusCode = response?.message?.statusCode;
        console.error('SIP registration rejected, status code:', statusCode);
        this.registrationState$.next('error');
        this.error$.next(registrationRejectionKey(statusCode));
    }

    /**
     * #338: sprawdź dane konta SIP próbną rejestracją w centrali. To jedyny sposób
     * weryfikacji hasła — SuperVOIP nie udostępnia go przez API, więc bez tego złe
     * hasło wchodzi do bazy i wychodzi dopiero martwym telefonem ratownika (339).
     *
     * Rejestruje się WŁASNYM UserAgentem i sprząta po sobie, żeby nie ruszyć telefonu
     * operatora, który akurat pracuje na tej samej karcie.
     */
    async verifyCredentials(credentials: SipCredentials): Promise<SipCredentialsCheck> {
        const server = credentials.sipServer || SipService.DEFAULT_SIP_SERVER;
        const uri = SIP.UserAgent.makeURI(`sip:${credentials.sipLogin}@${server}`);
        if (!uri) {
            console.error('SIP verify: nieprawidłowy adres konta', credentials.sipLogin);
            return 'unreachable';
        }

        const agent = this.createUserAgent({
            uri: uri,
            transportOptions: {
                server: `wss://${server}:${credentials.wsPort || SipService.DEFAULT_WS_PORT}`
            },
            authorizationUsername: credentials.sipLogin,
            authorizationPassword: credentials.sipPassword
        });

        try {
            await agent.start();
            const registerer = this.createRegisterer(agent);
            const result = await this.awaitRegistrationResult(registerer);
            if (result === 'valid') {
                // Nie zostawiaj w centrali rejestracji założonej tylko na próbę. Gdy się
                // nie uda, wygaśnie sama — ale ślad w konsoli zostaje, bo taka rejestracja
                // wygląda potem jak działający telefon na koncie, którego nikt nie używa.
                try {
                    await registerer.unregister();
                } catch (error) {
                    console.warn('SIP verify: nie udało się wyrejestrować próbnej rejestracji', error);
                }
            }
            return result;
        } catch (error) {
            console.error('SIP verify: brak łączności z centralą', error);
            return 'unreachable';
        } finally {
            try {
                await agent.stop();
            } catch (error) {
                console.warn('SIP verify: nie udało się zamknąć połączenia z centralą', error);
            }
        }
    }

    // Wydzielone, żeby test mógł podstawić własne obiekty sip.js — inaczej sprzątanie
    // po próbnej rejestracji (wyrejestrowanie, zamknięcie transportu) nie ma jak być sprawdzone.
    protected createUserAgent(options: any): any {
        return new SIP.UserAgent(options);
    }

    protected createRegisterer(agent: any): any {
        return new SIP.Registerer(agent);
    }

    protected createInviter(targetUri: any): any {
        return new SIP.Inviter(this.userAgent, targetUri, {
            sessionDescriptionHandlerOptions: {
                constraints: {
                    audio: true,
                    video: false
                }
            }
        });
    }

    protected createRemoteStream(): MediaStream {
        return new MediaStream();
    }

    private awaitRegistrationResult(registerer: any): Promise<SipCredentialsCheck> {
        return new Promise<SipCredentialsCheck>(resolve => {
            // Centrala może nie odpowiedzieć w ogóle — bez limitu formularz wisiałby w nieskończoność.
            const timer = setTimeout(() => resolve('unreachable'), SipService.VERIFY_TIMEOUT_MS);
            const settle = (result: SipCredentialsCheck) => {
                clearTimeout(timer);
                resolve(result);
            };

            registerer.register({
                requestDelegate: {
                    onAccept: () => settle('valid'),
                    onReject: (response: any) => {
                        const statusCode = response?.message?.statusCode;
                        console.warn('SIP verify: centrala odrzuciła rejestrację, kod', statusCode);
                        // Tylko odrzucenie z powodu danych konta znaczy „złe hasło”; awaria
                        // centrali nie może blokować zapisu tak samo jak zła para login/hasło.
                        settle(classifyRegistrationRejection(statusCode) === 'bad-credentials'
                            ? 'rejected'
                            : 'unreachable');
                    }
                }
            }).catch((error: any) => {
                console.error('SIP verify: nie udało się wysłać rejestracji', error);
                settle('unreachable');
            });
        });
    }

    /**
     * Wykonaj połączenie wychodzące
     */
    async makeCall(phoneNumber: string): Promise<boolean> {
        if (!this.userAgent || !this.session) {
            console.error('SIP not initialized');
            return false;
        }

        try {
            const targetUri = SIP.UserAgent.makeURI(`sip:${phoneNumber}@${this.session.sipServer}`);

            if (!targetUri) {
                console.error('Failed to create target URI');
                return false;
            }

            const inviter = this.createInviter(targetUri);

            this.currentSession = inviter;
            this.setupSessionHandlers(inviter, 'outgoing', phoneNumber);

            this.callInfo$.next({
                state: 'connecting',
                direction: 'outgoing',
                remoteNumber: phoneNumber,
                startTime: null,
                duration: 0
            });

            await inviter.invite({
                requestDelegate: {
                    onReject: (response: any) => {
                        if (inviter === this.currentSession) {
                            this.callEndReason = classifyCallRejection(response?.message?.statusCode);
                        }
                    }
                }
            });
            return true;

        } catch (error) {
            console.error('Make call error:', error);
            this.callEndReason = 'failed';
            this.resetCallState();
            return false;
        }
    }

    /**
     * Odbierz połączenie przychodzące
     */
    async answerCall(): Promise<boolean> {
        if (!this.currentSession) {
            return false;
        }

        try {
            await this.currentSession.accept({
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: true,
                        video: false
                    }
                }
            });
            return true;
        } catch (error) {
            console.error('Answer call error:', error);
            return false;
        }
    }

    /**
     * Odrzuć połączenie
     */
    async rejectCall(): Promise<void> {
        if (this.currentSession) {
            this.callEndReason = 'operator_rejected';
            try {
                await this.currentSession.reject();
            } catch (error) {
                console.error('Reject call error:', error);
            }
        }
        this.resetCallState();
    }

    /**
     * Rozłącz połączenie
     */
    /**
     * Wycisz / włącz mikrofon w trwającej rozmowie (wyłącza lokalną ścieżkę audio).
     */
    setMute(muted: boolean): void {
        this.callMuted = muted;
        const session: any = this.currentSession;
        const pc = session && session.sessionDescriptionHandler && session.sessionDescriptionHandler.peerConnection;
        if (!pc) { return; }
        pc.getSenders().forEach((sender: any) => {
            if (sender.track && sender.track.kind === 'audio') {
                sender.track.enabled = !muted;
            }
        });
    }

    /** Czy rozmowa jest aktualnie wstrzymana (na hold). */
    isOnHold(): boolean {
        return this.callInfo$.value.state === 'on-hold';
    }

    /**
     * Czy trwa rozmowa (albo telefon właśnie dzwoni). Pytać PRZED twardym
     * przeładowaniem strony — `window.location.reload()` niszczy całą aplikację
     * razem z sesją WebRTC, więc rozmowa urywa się w połowie zdania.
     *
     * Stan `on-hold` też się liczy: rozmowa zawieszona ginie przy przeładowaniu
     * tak samo jak aktywna. `currentSession` sprawdzamy dodatkowo, bo sesja
     * istnieje już chwilę przed pierwszą aktualizacją `callInfo$`.
     */
    hasActiveCall(): boolean {
        return this.callInfo$.value.state !== 'idle' || !!this.currentSession;
    }

    async hangup(): Promise<void> {
        if (this.currentSession) {
            try {
                const state = this.currentSession.state;
                if (state === SIP.SessionState.Established) {
                    await this.currentSession.bye();
                } else if (state === SIP.SessionState.Establishing) {
                    this.callEndReason = 'operator_cancelled';
                    // W sip.js 0.21 odebrane przychodzące (Invitation) ma tylko reject(), a cancel() ma wychodzące.
                    await (this.currentSession.cancel ? this.currentSession.cancel() : this.currentSession.reject());
                }
            } catch (error) {
                console.error('Hangup error:', error);
            }
        }
        this.resetCallState();
    }

    /**
     * Wstrzymaj/wznów rozmowę
     */
    async toggleHold(): Promise<boolean> {
        const session: any = this.currentSession;
        // W trakcie konsultacji hold dzwoniącego należy do przekazania (Połącz / Wróć).
        if (!session || this.consultSession) { return false; }

        const currentState = this.callInfo$.value.state;
        // Hold ma sens tylko w aktywnej lub już wstrzymanej rozmowie.
        if (currentState !== 'in-call' && currentState !== 'on-hold') { return false; }
        const goingOnHold = currentState === 'in-call';

        try {
            // sip.js 0.21 nie ma session.hold()/unhold() — realizujemy wstrzymanie lokalnie,
            // wyłączając audio w obie strony (jak przy wyciszeniu mikrofonu, sprawdzony sposób).
            const pc = session.sessionDescriptionHandler && session.sessionDescriptionHandler.peerConnection;
            if (pc) {
                // Tor wychodzący (mikrofon): na hold wyłącz; przy wznowieniu przywróć
                // tylko jeśli rozmowa nie była ręcznie wyciszona.
                pc.getSenders().forEach((s: any) => {
                    if (s.track && s.track.kind === 'audio') {
                        s.track.enabled = goingOnHold ? false : !this.callMuted;
                    }
                });
                // Tor przychodzący (głośnik): na hold wyłącz, żeby nie słyszeć rozmówcy.
                pc.getReceivers().forEach((r: any) => {
                    if (r.track && r.track.kind === 'audio') {
                        r.track.enabled = !goingOnHold;
                    }
                });
            }

            // Natychmiast odśwież stan (przycisk pauza/wznów) — nie czekając na re-INVITE.
            this.updateCallState(goingOnHold ? 'on-hold' : 'in-call');

            // Best-effort: powiadom drugą stronę przez re-INVITE (sendonly = hold).
            // Jeśli serwer nie wspiera renegocjacji, zostaje samo wstrzymanie lokalne.
            try {
                if (typeof session.invite === 'function') {
                    const holdModifier = (description: any) => {
                        if (description && description.sdp) {
                            description.sdp = description.sdp.replace(/a=sendrecv/g, 'a=sendonly');
                        }
                        return Promise.resolve(description);
                    };
                    await session.invite({
                        sessionDescriptionHandlerModifiers: goingOnHold ? [holdModifier] : []
                    });
                }
            } catch (reinviteErr) {
                console.warn('[VoIP] Hold re-INVITE failed, using local hold only:', reinviteErr);
            }

            return true;
        } catch (error) {
            console.error('Toggle hold error:', error);
            return false;
        }
    }

    /**
     * Wyślij DTMF (tony)
     */
    sendDtmf(digit: string): void {
        if (this.currentSession && this.currentSession.sessionDescriptionHandler) {
            try {
                this.currentSession.sessionDescriptionHandler.sendDtmf(digit);
            } catch (error) {
                console.error('DTMF error:', error);
            }
        }
    }

    // === Przekazywanie rozmowy (SIP REFER) — port z widgetu TC ===

    /**
     * REFER w ramach dialogu i CZEKANIE NA WYNIK. Centrala (SuperVOIP) potwierdza przyjęcie 202,
     * a rezultat raportuje w NOTIFY (sipfrag: „SIP/2.0 200 OK" = przekazane, 4xx/5xx = nie,
     * np. 480 agent niezalogowany, 486 zajęty). Test TC 4.09.2026: przy porażce centrala odgrywa
     * komunikat i ODDAJE rozmowę nam — dlatego nigdy nie rozłączamy własnej nogi „na wszelki
     * wypadek". Po NOTIFY 200 centrala trzyma naszą nogę jeszcze 30–45 s (zestawia rozmówcę
     * z celem) i zamyka ją sama BYE — własne BYE w tym oknie kończy CAŁĄ rozmowę.
     *
     * `referrer` — sesja, którą przekazujemy; `target` — URI numeru (blind) albo sesja (Replaces).
     */
    private referAndWait(referrer: any, target: any): Promise<TransferResult> {
        const targetLabel = (target && target.user) ? String(target.user) : 'session';
        const info = this.callInfo$.value;
        const log = (step: string, code?: number, detail?: string) => {
            this.workstationService.logTransfer({
                step: step, target: targetLabel, phone: info.remoteNumber, direction: info.direction,
                code: code || 0, detail: detail || ''
            });
        };
        const startedAt = Date.now();
        return new Promise<TransferResult>(resolve => {
            let settled = false;
            let timer: any = null;
            const finish = (result: TransferResult) => {
                if (settled) { return; }
                settled = true;
                if (timer) { clearTimeout(timer); }
                try { referrer.stateChange.removeListener(onState); } catch (e) {}
                log('result', result.code, (result.ok ? 'OK ' : 'FAIL ') + (result.reason || '') + ' +' + (Date.now() - startedAt) + 'ms');
                this.ngZone.run(() => resolve(result));
            };
            log('refer.send');
            // Centrala zamknęła naszą nogę przed finalnym NOTIFY — przejęła rozmowę, wynik zna tylko CDR.
            const onState = (state: any) => {
                if (state === SIP.SessionState.Terminated) {
                    log('session.terminated', 0, '+' + (Date.now() - startedAt) + 'ms');
                    finish({ ok: true, code: 0 });
                }
            };
            try { referrer.stateChange.addListener(onState); } catch (e) {}

            // Bez finalnego NOTIFY po 30 s nie zgadujemy — rozmowa zostaje u nas.
            timer = setTimeout(() => finish({ ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_NO_CONFIRM' }), 30000);

            const options = {
                requestDelegate: {
                    onAccept: (response: any) => {
                        log('refer.accepted', response?.message?.statusCode, '+' + (Date.now() - startedAt) + 'ms');
                    },
                    onReject: (response: any) => {
                        const code = response?.message?.statusCode || 0;
                        log('refer.rejected', code, response?.message?.reasonPhrase || '');
                        finish({ ok: false, code: code, reason: 'VOIP.TRANSFER_ERR_FAILED' });
                    }
                },
                onNotify: (notification: any) => {
                    try { notification.accept(); } catch (e) {}
                    const body = notification?.request?.body || '';
                    const m = /SIP\/2\.0\s+(\d{3})/.exec(body);
                    log('notify', m ? parseInt(m[1], 10) : 0, String(body).replace(/\s+/g, ' ').trim());
                    if (!m) { return; }
                    const code = parseInt(m[1], 10);
                    if (code < 200) { return; }            // 100 Trying / 180 Ringing — czekamy
                    if (code < 300) { finish({ ok: true, code: code }); return; }
                    finish({ ok: false, code: code, reason: this.transferFailureKey(code) });
                }
            };

            try {
                referrer.refer(target, options).catch((error: any) => {
                    console.error('REFER error:', error);
                    finish({ ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_FAILED' });
                });
            } catch (error) {
                console.error('REFER error:', error);
                finish({ ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_FAILED' });
            }
        });
    }

    private transferFailureKey(code: number): string {
        switch (code) {
            case 404: return 'VOIP.TRANSFER_ERR_NOT_FOUND';
            case 408:
            case 480:
            case 503: return 'VOIP.TRANSFER_ERR_UNAVAILABLE';
            case 486:
            case 600: return 'VOIP.TRANSFER_ERR_BUSY';
            case 603: return 'VOIP.TRANSFER_ERR_DECLINED';
            default: return 'VOIP.TRANSFER_ERR_FAILED';
        }
    }

    /** Przekazanie „w ciemno" bieżącej rozmowy na numer WEWNĘTRZNY (agent CRK). */
    async transferBlind(number: string): Promise<TransferResult> {
        const session: any = this.currentSession;
        if (!session || session.state !== SIP.SessionState.Established || !this.session) {
            return { ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_NO_CALL' };
        }
        const target = SIP.UserAgent.makeURI(`sip:${number}@${this.session.sipServer}`);
        if (!target) {
            return { ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_FAILED' };
        }
        return this.referAndWait(session, target);
    }

    /**
     * Przekazanie z konsultacją, krok 1: dzwoniący na hold, my dzwonimy do odbiorcy drugą nogą.
     * Dalej „Połącz" (completeConsultTransfer) albo „Wróć" (cancelConsultTransfer).
     */
    async startConsultTransfer(number: string): Promise<boolean> {
        if (!this.session || this.consultSession || this.callInfo$.value.state !== 'in-call') {
            return false;
        }
        const targetUri = SIP.UserAgent.makeURI(`sip:${number}@${this.session.sipServer}`);
        if (!targetUri || !(await this.toggleHold())) {
            return false;
        }

        const consult = this.createInviter(targetUri);
        this.consultSession = consult;
        this.consultTransferred = false;
        this.consultCall$.next({ number: number, state: 'connecting' });

        consult.stateChange.addListener((state: any) => {
            this.ngZone.run(() => {
                if (state === SIP.SessionState.Established && consult === this.consultSession) {
                    this.consultCall$.next({ number: number, state: 'in-call' });
                    this.attachAudio(consult);
                } else if (state === SIP.SessionState.Terminated) {
                    this.onConsultEnded(consult);
                }
            });
        });

        try {
            await consult.invite();
            return true;
        } catch (error) {
            console.error('Consult call error:', error);
            this.onConsultEnded(consult);
            return false;
        }
    }

    /** Krok 2: dzwoniący dostaje REFER z Replaces na naszą rozmowę z odbiorcą — centrala łączy ich ze sobą. */
    async completeConsultTransfer(): Promise<TransferResult> {
        const caller: any = this.currentSession;
        const consult: any = this.consultSession;
        if (!caller || caller.state !== SIP.SessionState.Established
            || !consult || consult.state !== SIP.SessionState.Established) {
            return { ok: false, code: 0, reason: 'VOIP.TRANSFER_ERR_NO_CALL' };
        }
        this.consultTransferred = true;
        const result = await this.referAndWait(caller, consult);
        if (!result.ok) {
            this.consultTransferred = false;
        }
        return result;
    }

    /** „Wróć do dzwoniącego": rozłącz odbiorcę i zdejmij dzwoniącego z holdu. */
    cancelConsultTransfer(): void {
        if (!this.consultSession) { return; }
        this.dropConsult();
        this.resumeAfterConsult();
    }

    getConsultCall(): Observable<ConsultCall | null> {
        return this.consultCall$.asObservable();
    }

    /** Odbiorca się rozłączył albo nie odebrał — wracamy do dzwoniącego (chyba że „Połącz" go przekazał). */
    private onConsultEnded(consult: any): void {
        if (consult !== this.consultSession) { return; }
        this.consultSession = null;
        this.consultCall$.next(null);
        if (!this.consultTransferred) {
            this.resumeAfterConsult();
        }
    }

    private resumeAfterConsult(): void {
        if (this.currentSession && this.callInfo$.value.state === 'on-hold') {
            this.attachAudio(this.currentSession);
            this.toggleHold();
        }
    }

    /** Rozłącz drugą nogę bez wznawiania dzwoniącego — o tym decyduje wołający. */
    private dropConsult(): void {
        const consult = this.consultSession;
        if (!consult) { return; }
        this.consultSession = null;
        this.consultCall$.next(null);
        try {
            if (consult.state === SIP.SessionState.Established) {
                consult.bye().catch(() => {});
            } else if (consult.state !== SIP.SessionState.Terminated) {
                consult.cancel().catch(() => {});
            }
        } catch (error) {
            console.error('Consult hangup error:', error);
        }
    }

    /**
     * Wyrejestruj i zamknij
     */
    async disconnect(): Promise<void> {
        this.stopDurationTimer();
        this.cancelReconnect();

        // Remove beforeunload listener
        if (this.boundBeforeUnload) {
            window.removeEventListener('beforeunload', this.boundBeforeUnload);
            this.boundBeforeUnload = null;
        }

        // #372: nie zostawiaj oczekujących dzwoniących wiszących na trunku po wylogowaniu
        // ze stanowiska — odrzuć je jawnie i zaraportuj jako nieodebrane.
        const waiting = this.waitingSessions.slice();
        this.waitingSessions = [];
        this.publishWaitingCalls();
        for (const entry of waiting) {
            try {
                if (entry.invitation.state !== SIP.SessionState.Terminated) {
                    await entry.invitation.reject();
                }
            } catch (e) {}
            this.missedCall$.next({ remoteNumber: entry.remoteNumber, receivedAt: entry.receivedAt, endReason: null });
        }

        if (this.currentSession) {
            try {
                await this.hangup();
            } catch (e) {}
        }

        if (this.registerer) {
            try {
                await this.registerer.unregister();
            } catch (e) {}
        }

        if (this.userAgent) {
            try {
                await this.userAgent.stop();
            } catch (e) {}
        }

        this.userAgent = null;
        this.registerer = null;
        this.session = null;
        this.registrationState$.next('unregistered');
    }

    // Gettery dla observables
    getRegistrationState(): Observable<RegistrationState> {
        return this.registrationState$.asObservable();
    }

    getCallInfo(): Observable<CallInfo> {
        return this.callInfo$.asObservable();
    }

    getIncomingCall(): Observable<{ remoteNumber: string }> {
        return this.incomingCall$.asObservable();
    }

    /** #372: połączenia oczekujące (przyszły gdy telefon był zajęty). */
    getWaitingCalls(): Observable<WaitingCall[]> {
        return this.waitingCalls$.asObservable();
    }

    /** #372: oczekujący dzwoniący, który się rozłączył / został odrzucony — do zalogowania jako nieodebrane. */
    getMissedCall(): Observable<{ remoteNumber: string; receivedAt: Date; endReason: CallEndReason | null }> {
        return this.missedCall$.asObservable();
    }

    /** Klucz i18n z powodem, dla którego telefon nie działa (do pokazania operatorowi). */
    getError(): Observable<string> {
        return this.error$.asObservable();
    }

    getDialRequest(): Observable<{ phoneNumber: string; name?: string }> {
        return this.dialRequest$.asObservable();
    }

    isVoipAvailable(): boolean {
        return this.voipAvailable;
    }

    markVoipAvailable(): void {
        this.voipAvailable = true;
    }

    getCallEnded(): Observable<{ remoteNumber: string; direction: CallDirection; duration: number; startTime: Date | null; endReason: CallEndReason | null }> {
        return this.callEnded$.asObservable();
    }

    getCurrentSession(): WorkstationSession | null {
        return this.session;
    }

    isRegistered(): boolean {
        return this.registrationState$.value === 'registered';
    }

    /**
     * Żądanie wybrania numeru z zewnętrznego komponentu (click-to-call)
     * Otwiera widget telefonu i ustawia numer do wybrania
     */
    requestDial(phoneNumber: string, name?: string): void {
        // Wyczyść numer z niepotrzebnych znaków
        const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
        console.log('SIP: Dial request for:', cleanNumber, name);
        this.dialRequest$.next({ phoneNumber: cleanNumber, name });
    }

    /**
     * Sprawdź czy można wykonać połączenie
     */
    canDial(): boolean {
        return this.isRegistered() && this.callInfo$.value.state === 'idle';
    }

    // === Metody prywatne ===

    private handleIncomingCall(invitation: any): void {
        console.log('>>> INCOMING CALL <<<', invitation);

        const remoteNumber = invitation.remoteIdentity?.uri?.user || 'Unknown';
        console.log('Incoming call from:', remoteNumber);

        // #372: telefon zajęty (dzwoni albo trwa rozmowa) → do kolejki, NIE nadpisujemy sesji.
        if (this.currentSession || this.callInfo$.value.state !== 'idle') {
            this.enqueueWaitingCall(invitation, remoteNumber);
            return;
        }

        this.presentIncomingCall(invitation, remoteNumber);
    }

    /** Wystaw połączenie przychodzące na telefonie (dzwonek + stan 'ringing'). */
    private presentIncomingCall(invitation: any, remoteNumber: string): void {
        this.currentSession = invitation;
        invitation.delegate = {
            onCancel: () => { this.callEndReason = 'caller_hung_up'; }
        };
        this.setupSessionHandlers(invitation, 'incoming', remoteNumber);

        this.callInfo$.next({
            state: 'ringing',
            direction: 'incoming',
            remoteNumber: remoteNumber,
            startTime: null,
            duration: 0
        });

        this.incomingCall$.next({ remoteNumber });
    }

    /**
     * #372: odłóż połączenie do kolejki oczekujących. Sesja SIP zostaje nietknięta (dzwoni dalej
     * u siebie), a gdy dzwoniący się rozłączy albo trunk go zerwie — zdejmujemy go z kolejki
     * i raportujemy jako nieodebrane, żeby nie zniknął bez śladu.
     */
    private enqueueWaitingCall(invitation: any, remoteNumber: string): void {
        const entry = { id: ++this.waitingCallSeq, invitation, remoteNumber, receivedAt: new Date() };
        this.waitingSessions.push(entry);
        this.publishWaitingCalls();
        console.log('[VoIP] #372 telefon zajęty — połączenie do kolejki:', remoteNumber);

        invitation.stateChange.addListener((state: any) => {
            if (state !== SIP.SessionState.Terminated) { return; }
            this.ngZone.run(() => {
                // Jeśli w międzyczasie awansowało na telefon, jego zakończeniem zajmuje się
                // już zwykły handler sesji — tu tylko sprzątamy oczekujących.
                if (!this.removeWaitingCall(entry.id)) { return; }
                this.missedCall$.next({ remoteNumber: entry.remoteNumber, receivedAt: entry.receivedAt, endReason: 'caller_hung_up_waiting' });
            });
        });
    }

    /** Zdejmij z kolejki; zwraca true jeśli wpis tam jeszcze był. */
    private removeWaitingCall(id: number): boolean {
        const before = this.waitingSessions.length;
        this.waitingSessions = this.waitingSessions.filter(w => w.id !== id);
        const removed = this.waitingSessions.length !== before;
        if (removed) { this.publishWaitingCalls(); }
        return removed;
    }

    private publishWaitingCalls(): void {
        this.waitingCalls$.next(this.waitingSessions.map(w => ({
            id: w.id,
            remoteNumber: w.remoteNumber,
            receivedAt: w.receivedAt
        })));
    }

    /**
     * Po zwolnieniu telefonu wystaw pierwsze oczekujące połączenie, które jeszcze żyje.
     * Bez tego drugi dzwoniący nigdy nie wracał na ekran (#372).
     */
    private promoteNextWaitingCall(): void {
        while (this.waitingSessions.length) {
            const next = this.waitingSessions.shift();
            this.publishWaitingCalls();
            const state = next.invitation && next.invitation.state;
            if (state === SIP.SessionState.Terminated) {
                // Zdążył się rozłączyć czekając — zgłoś jako nieodebrane i próbuj następnego.
                this.missedCall$.next({ remoteNumber: next.remoteNumber, receivedAt: next.receivedAt, endReason: 'caller_hung_up_waiting' });
                continue;
            }
            console.log('[VoIP] #372 telefon wolny — wystawiam oczekujące połączenie:', next.remoteNumber);
            this.presentIncomingCall(next.invitation, next.remoteNumber);
            return;
        }
    }

    /** Odrzuć konkretne połączenie z kolejki oczekujących. */
    async rejectWaitingCall(id: number): Promise<void> {
        const entry = this.waitingSessions.find(w => w.id === id);
        if (!entry) { return; }
        this.removeWaitingCall(id);
        try {
            if (entry.invitation.state !== SIP.SessionState.Terminated) {
                await entry.invitation.reject();
            }
        } catch (error) {
            console.error('Reject waiting call error:', error);
        }
        this.missedCall$.next({ remoteNumber: entry.remoteNumber, receivedAt: entry.receivedAt, endReason: 'operator_rejected' });
    }

    private setupSessionHandlers(session: any, direction: CallDirection, remoteNumber: string): void {
        session.stateChange.addListener((state: any) => {
            // #372: zmiany stanu sesji, która NIE jest już aktywnym połączeniem (np. porzucony
            // dzwoniący gaśnie w trakcie kolejnej rozmowy), nie mogą ruszać stanu telefonu —
            // wcześniej takie Terminated wywoływało resetCallState i ucinało trwającą rozmowę.
            if (session !== this.currentSession) { return; }
            this.ngZone.run(() => {
                switch (state) {
                    case SIP.SessionState.Establishing:
                        this.updateCallState('connecting');
                        break;
                    case SIP.SessionState.Established:
                        this.callInfo$.next({
                            state: 'in-call',
                            direction: direction,
                            remoteNumber: remoteNumber,
                            startTime: new Date(),
                            duration: 0
                        });
                        this.attachAudio(session);
                        // Wymuś bieżący stan wyciszenia, gdy tory audio są już gotowe
                        // (gdyby ktoś kliknął „wycisz" zanim peerConnection wstał).
                        if (this.callMuted) { this.setMute(true); }
                        this.startDurationTimer();
                        break;
                    case SIP.SessionState.Terminated:
                        if (direction === 'outgoing') {
                            // sip.js 0.21 podaje kod odrzucenia (requestDelegate.onReject) dopiero po Terminated.
                            Promise.resolve().then(() => {
                                if (session === this.currentSession) { this.resetCallState(); }
                            });
                        } else {
                            this.resetCallState();
                        }
                        break;
                }
            });
        });
    }

    private attachAudio(session: any): void {
        if (!this.audioElement) return;

        const remoteStream = this.createRemoteStream();

        const pc = session.sessionDescriptionHandler?.peerConnection;
        if (pc) {
            pc.getReceivers().forEach((receiver: any) => {
                if (receiver.track) {
                    remoteStream.addTrack(receiver.track);
                }
            });
        }

        this.audioElement.srcObject = remoteStream;
        this.audioElement.play().catch(e => console.error('Audio play error:', e));
    }

    private updateCallState(state: CallState): void {
        this.callInfo$.next({
            ...this.callInfo$.value,
            state: state
        });
    }

    private resetCallState(): void {
        // Emituj callEnded$ PRZED wyczyszczeniem stanu (tylko jeśli była aktywna rozmowa)
        const current = this.callInfo$.value;
        if (current.state !== 'idle' && current.remoteNumber) {
            this.callEnded$.next({
                remoteNumber: current.remoteNumber,
                direction: current.direction,
                duration: current.duration,
                startTime: current.startTime,
                endReason: this.callEndReason
            });
        }

        this.stopDurationTimer();
        // Dzwoniący zniknął (rozłączony albo przekazany) — druga noga konsultacji nie ma już sensu.
        this.dropConsult();
        this.currentSession = null;
        this.callEndReason = null;
        this.callMuted = false; // nowa rozmowa nie powinna dziedziczyć wyciszenia z poprzedniej

        if (this.audioElement) {
            this.audioElement.srcObject = null;
        }

        this.callInfo$.next({
            state: 'idle',
            direction: null,
            remoteNumber: null,
            startTime: null,
            duration: 0
        });

        // #372: telefon jest wolny — wystaw pierwszego z kolejki oczekujących.
        this.promoteNextWaitingCall();
    }

    private startDurationTimer(): void {
        this.stopDurationTimer();

        this.durationInterval = setInterval(() => {
            const callInfo = this.callInfo$.value;
            if (callInfo.startTime && callInfo.state === 'in-call') {
                const duration = Math.floor((new Date().getTime() - callInfo.startTime.getTime()) / 1000);
                this.callInfo$.next({
                    ...callInfo,
                    duration: duration
                });
            }
        }, 1000);
    }

    private stopDurationTimer(): void {
        if (this.durationInterval) {
            clearInterval(this.durationInterval);
            this.durationInterval = null;
        }
    }
}
